/*
 * Copyright (c) 2021-2024. StockAnywhere CC
 * StockAnywhere - https://www.stockanywhere.io
 * View license agreement and terms of use at https://www.stockanywhere.io/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-06-30
 */

import React, { useState } from "react";
import { IQueryTableColumnDetails, ColumnSort } from "../Interfaces";
import { ArrowUpDownSplit } from "./ArrowUpDownSplit";

interface ListColumnsProps {
  columnDetails: IQueryTableColumnDetails[];
  sortState: ColumnSort[];
  setSortState: React.Dispatch<React.SetStateAction<ColumnSort[]>>;
  sortableColumns?: { name: string; type: string; sortable: boolean; searchable: boolean; }[] | null
}


export const ListColumns: React.FC<ListColumnsProps> = ({ columnDetails, sortState, setSortState, sortableColumns }) => {


    let i: number = 0;
    const thStyle: React.CSSProperties = {
        border: '1px solid #deebed',
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap" as const,
        backgroundColor: "#f8f9fa",
    };

    // change column names for display
    function transformString(inputString:string) {
        // Check if the input is a string
        if (typeof inputString !== 'string') {
            return inputString;
        }

        // Remove underscores and capitalize letters following each underscore
        const result = inputString
          .split('_') // Split the string by underscores
          .map((word, index) => {
            // Capitalize the first letter of each word after the first
            return index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);
          })
          .join(' '); // Join all the words back together
      
        // Ensure the first letter is capitalized
        return result.charAt(0).toUpperCase() + result.slice(1);
    }


    //testing cycling sort
    const cycleSort = (prev: ColumnSort[], column: string): ColumnSort[] => {
      const existing = prev.find(c => c.column === column);

      if (!existing) {
        // Start with ascending
        return [...prev, { column, direction: "asc" }];
      }

      if (existing.direction === "asc") {
        // Then go descending
        return prev.map(c =>
          c.column === column ? { ...c, direction: "desc" } : c
        );
      }

      if (existing.direction === "desc") {
        // Finally go to null (unsorted)
        return prev.map(c =>
          c.column === column ? { ...c, direction: null } : c
        );
      }

      // If direction is null, restart the cycle at asc
      return prev.map(c =>
        c.column === column ? { ...c, direction: "asc" } : c
      );
    };

     const mergedColumns = columnDetails.map((col) => {
       const sortableConfig = sortableColumns?.find((c) => c.name === col.name);
       return { ...col, isSortable: sortableConfig?.sortable ?? false };
    });


    const columns: React.ReactElement[] = [];

    mergedColumns.forEach((column, i) => {
       const colName = column.name;
       if (!colName) return;

      const fullColName = colName.toLowerCase();
      if (["notifications.reference", "id"].includes(fullColName)) return;

      const displayName = fullColName === "assets.url" ? "Image" : transformString(colName);

      const colSort = sortState.find((c) => c.column === colName);
      const isActive = !!colSort;
      const direction = colSort?.direction ?? null;

      const handleSortClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (column.isSortable) {
          setSortState((prev) => cycleSort(prev, colName));
        }
      };

      columns.push( <th key={i} scope="col" style={{...thStyle, backgroundColor: isActive ? "#f1f8ff" : "#f8f9fa", verticalAlign: "top", position: "relative" }}>
        <div className="th-wrapper compact-sort" style={{ paddingTop: "0.2rem" }}>
          <span className="th-name">{displayName}</span>
          {column.isSortable && (
            <span className="th-icons-vertical">
              <span className="icon-pointer single" onClick={handleSortClick} style={{ fontSize: "1rem" }}>
                {direction === "asc" ? (
                  <ArrowUpDownSplit upColor="red" downColor="#ccc" />
                ) : direction === "desc" ? (
                  <ArrowUpDownSplit upColor="#ccc" downColor="red" />
                ) : (
                  <ArrowUpDownSplit upColor="#777" downColor="#777" />
                )}
              </span>
            </span>
          )}
        </div>
      </th>
    );
  });


    // if (Array.isArray(columnDetails)) {
    //   columnDetails.forEach((column: IQueryTableColumnDetails, i) => {
    //     const colName = column.name;
    //     const nameString = String(colName || "").trim();
    //     const fullColName = nameString.toLowerCase();

    //     // Skip unwanted columns
    //     if (!colName || ["notifications.reference", "id"].includes(fullColName)) return;

    //     const displayName = fullColName === "assets.url" ? "Image" : transformString(colName);

    //     // Sorting info
    //     const colSort = sortState.find((c) => c.column === colName);
    //     const isActive = !!colSort;
    //     const direction = colSort?.direction ?? null;

    //     const handleSortClick = (e: React.MouseEvent) => {
    //       e.stopPropagation();
    //       setSortState((prev) => cycleSort(prev, colName));
    //     };

    //     // <-- check custom column

    //     columns.push(
    //       <th key={i} scope="col" style={{ ...thStyle, backgroundColor: isActive ? "#f1f8ff" : "#f8f9fa",verticalAlign: "top", position: "relative"}}>
    //         <div className="th-wrapper compact-sort" style={{ paddingTop: "0.2rem" }}>
    //           <span className="th-name">{displayName}</span>
    //           {/* Sorting icons only if not a custom column */}
    //           {!isCustom && (
    //             <span className="th-icons-vertical" onClick={(e) => e.stopPropagation()}>
    //               <span
    //                 className="icon-pointer single"
    //                 onClick={handleSortClick}
    //                 style={{ fontSize: "1rem" }}
    //               >
    //                 {direction === "asc" ? (
    //                   <ArrowUpDownSplit upColor="red" downColor="#ccc" />
    //                 ) : direction === "desc" ? (
    //                   <ArrowUpDownSplit upColor="#ccc" downColor="red" />
    //                 ) : (
    //                   <ArrowUpDownSplit upColor="#777" downColor="#777" />
    //                 )}
    //               </span>
    //             </span>
    //           )}
    //         </div>
    //       </th>
    //     );
    //   });
    // }


  
    return(
        <>
        <thead className="sa-list-columns">
            <tr>                             
                {columns}                 
                {/* render action column when there as actual columns */}
                {columns.length > 0 && (
                  <th className="sa-record-control sa-export-selected-records" style={{ ...thStyle, paddingBottom: "0.8rem", width: "100px" }}>
                    Actions
                  </th>
                )}                                      
            </tr>
        </thead>
        </>
    );
}


 // Data
    // if (Array.isArray(columnDetails)) {
    //   columnDetails.forEach((column: IQueryTableColumnDetails, i) => {
    //   const columnName = column.name;
    //   const fullColumnName = columnName?.toLowerCase();
    //   if (!columnName || ["notifications.reference", "id"].includes(fullColumnName)) return;

    //   const displayName = fullColumnName === "assets.url" ? "Image" : transformString(columnName);

    //   // Get this column's sort info
    //   const colSort = sortState.find(c => c.column === column.name);
    //   const isActive = !!colSort;
    //   const direction = colSort?.direction ?? null;

    //   columns.push(
    //      <th key={i} scope="col" style={{...thStyle, backgroundColor: isActive ? "#f1f8ff" : "#f8f9fa", verticalAlign: "top", position: "relative" }}>
    //       <div className={`th-wrapper compact-sort`} style={{paddingTop: "0.2rem"}}>
    //         <span className="th-name">{displayName}</span>
    //         {/* Vertical stacked icons */}
    //         <span className="th-icons-vertical" onClick={(e) => e.stopPropagation()}>
    //            <span className="icon-pointer single" onClick={(e) => {
    //               e.stopPropagation();
    //               setSortState(prev => cycleSort(prev, column.name));
    //             }}
    //             style={{ fontSize: "1rem" }}
    //           >
    //           {direction === "asc" ? (
    //             <ArrowUpDownSplit upColor="red" downColor="#ccc" />
    //           ) : direction === "desc" ? (
    //             <ArrowUpDownSplit upColor="#ccc" downColor="red" />
    //           ) : (
    //             <ArrowUpDownSplit upColor="#777" downColor="#777" />
    //           )}
    //           </span>
    //         </span>
    //       </div>
    //     </th>
    //   );
    //  });
    // }